/**
 * De toast met een knop (ontwerp v4): een zwarte pil boven de menubalk
 * (zonder menubalk lager, altijd boven de safe area) met een korte zin en
 * hooguit één knop (Ongedaan maken, Bekijk). Hij verdwijnt vanzelf na
 * --dur-lees, met een knop na --dur-lees-handeling (je moet hem ook kunnen
 * halen); zolang de muis erop staat of de focus erin, wacht hij.
 *
 * Nooit de focus pakken: de plek is altijd in de DOM (role=status, dus
 * beleefd hardop), de toast verschijnt erin. De knop is gewoon met Tab te
 * bereiken, ook als er een laag open is (lagen.ts neemt hem mee in de
 * ronde); verdwijnt de pil met de focus erin, dan gaat die terug naar waar
 * hij vandaan kwam. Eén toast tegelijk: een nieuwe vervangt de vorige.
 *
 * Los van de sonner-toasts van de server (EpicToaster, root.tsx): die
 * blijven zoals ze zijn. Ingang en uitgang met transform en opacity
 * (heute.css), op --dur en --dur-exit; verminderde beweging zet ze stil.
 *
 * Gebruik: `const { toon } = useHeuteToast(); toon({ tekst, knop })`, en
 * <HeuteToaster /> staat één keer in root.tsx.
 *
 * W13 (meldingen als regel): op Vandaag is een melding over je status geen
 * pil maar een tijdelijke regel onder de gezichten (vandaag-regel.tsx).
 * Alleen een toast met `plek: 'regel'` (de statusmeldingen van Vandaag)
 * gaat daarheen, en alleen zolang zo'n regelplek actief is (useRegelPlek).
 * Al het andere (Toch wel, Ongedaan maken, Ander moment na een veeg) blijft
 * de vaste pil onderaan: die staat altijd in beeld, ook als je gescrold
 * hebt. De leestijd en het wachten blijven hier, in één winkel, zodat beide
 * vormen dezelfde tijd houden. `sub` en `teken` tekent alleen de regel.
 */
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
	useSyncExternalStore,
} from 'react'
import { LEESTIJD } from '#app/generated/design-tokens/gebaar.ts'
import { animatieDuur } from './lagen.ts'

export type ToastKnop = { label: string; doe: () => void }
export type ToastInhoud = {
	tekst: string
	knop?: ToastKnop
	/** Een tweede regel, klein (alleen de regel op Vandaag). */
	sub?: string
	/** Een teken vooraan (alleen de regel op Vandaag): ✓ of ×. */
	teken?: 'go' | 'uit'
	/** 'regel': een statusmelding van Vandaag, die als regel onder de
	 *  gezichten staat zolang Vandaag voor ligt (anders de pil). */
	plek?: 'regel'
}
export type Toast = ToastInhoud & { id: number }

let huidige: Toast | null = null
let teller = 0
/** Je bent erbij (muis erop, focus erin): de leestijd wacht. */
let wacht = false
/** Hoeveel regelplekken er nu actief zijn (Vandaag, niet achter een laag). */
let regelPlekken = 0
const luisteraars = new Set<() => void>()

function meld() {
	for (const luisteraar of luisteraars) luisteraar()
}

function abonneer(luisteraar: () => void) {
	luisteraars.add(luisteraar)
	return () => {
		luisteraars.delete(luisteraar)
	}
}

/** Toont een toast (en vervangt wat er stond). Geeft zijn id. */
export function toonToast(inhoud: ToastInhoud): number {
	teller += 1
	huidige = { ...inhoud, id: teller }
	wacht = false
	meld()
	return teller
}

/** Je bent erbij of niet meer: de leestijd wacht, of loopt opnieuw. */
export function zetToastWacht(aan: boolean) {
	if (wacht === aan) return
	wacht = aan
	meld()
}

/** De toast die nu staat (voor de regel op Vandaag). */
export function useHuidigeToast(): Toast | null {
	return useSyncExternalStore(
		abonneer,
		() => huidige,
		() => null,
	)
}

/** Een regelplek meldt zich aan zolang hij `actief` is: dan geen pil. */
export function useRegelPlek(actief: boolean) {
	useEffect(() => {
		if (!actief) return
		regelPlekken += 1
		meld()
		return () => {
			regelPlekken -= 1
			meld()
		}
	}, [actief])
}

/** Haalt toast `id` weg (of welke er ook staat, zonder id). */
export function verbergToast(id?: number) {
	if (!huidige || (id !== undefined && huidige.id !== id)) return
	huidige = null
	meld()
}

const toonHook = { toon: toonToast, verberg: verbergToast }

export function useHeuteToast(): {
	toon: (inhoud: ToastInhoud) => number
	verberg: (id?: number) => void
} {
	return toonHook
}

/** Hoe lang hij staat: langer met een knop. */
export function leestijd(inhoud: ToastInhoud): number {
	return inhoud.knop ? LEESTIJD.leesHandeling : LEESTIJD.lees
}

export function HeuteToaster() {
	const toast = useSyncExternalStore(
		abonneer,
		() => huidige,
		() => null,
	)
	const wachtNu = useSyncExternalStore(
		abonneer,
		() => wacht,
		() => false,
	)
	const regelPlekActief = useSyncExternalStore(
		abonneer,
		() => regelPlekken > 0,
		() => false,
	)
	// de laatste toast blijft getekend tijdens zijn uitgang
	const [getoond, zetGetoond] = useState<Toast | null>(toast)
	const [uit, zetUit] = useState(false)
	if (toast && toast !== getoond) {
		zetGetoond(toast)
		zetUit(false)
	}
	if (!toast && getoond && !uit) zetUit(true)
	// alleen een statusmelding staat als regel op Vandaag
	const alsRegel = regelPlekActief && getoond?.plek === 'regel'

	const pilRef = useRef<HTMLDivElement>(null)
	// waar de focus vandaan kwam toen hij de pil in ging; staat hij er nog in
	// als de pil weggaat (of een nieuwe hem vervangt), dan gaat hij daarheen
	// terug in plaats van naar het niets
	const herkomstRef = useRef<HTMLElement | null>(null)
	const focusErinRef = useRef(false)
	const pilIdRef = useRef<number | null>(null)

	useLayoutEffect(() => {
		if (!focusErinRef.current) return
		const pil = pilRef.current
		const actief = document.activeElement
		const nogErin = Boolean(pil && actief && pil.contains(actief))
		const kwijt = !actief || actief === document.body
		if (!(uit && nogErin) && !(kwijt && getoond?.id !== pilIdRef.current))
			return
		focusErinRef.current = false
		const herkomst = herkomstRef.current
		herkomstRef.current = null
		if (herkomst?.isConnected && !herkomst.closest('[inert]'))
			herkomst.focus({ preventScroll: true })
		else if (nogErin) (actief as HTMLElement).blur()
	}, [uit, getoond])

	// vanzelf weg, behalve zolang je erbij bent; daarna weer de hele tijd
	// (ook als de toast als regel op Vandaag staat)
	useEffect(() => {
		if (!toast || wachtNu) return
		const timer = window.setTimeout(
			() => verbergToast(toast.id),
			leestijd(toast),
		)
		return () => window.clearTimeout(timer)
	}, [toast, wachtNu])

	const weg = useCallback(() => {
		zetGetoond(null)
		zetUit(false)
		zetToastWacht(false)
	}, [])

	// de uitgang: klaar aan het eind van zijn animatie, met een vangnet
	useEffect(() => {
		if (!uit) return
		const el = pilRef.current
		const timer = window.setTimeout(weg, el ? animatieDuur(el) * 1.5 : 0)
		return () => window.clearTimeout(timer)
	}, [uit, weg])

	return (
		<div className="heute-toast-plek" role="status">
			{getoond && !alsRegel ? (
				<div
					ref={pilRef}
					key={getoond.id}
					className="heute-toast"
					data-sfeer="inkt"
					data-uit={uit || undefined}
					data-testid="heute-toast"
					onAnimationEnd={(e) => {
						if (e.target === e.currentTarget && uit) weg()
					}}
					onPointerEnter={() => zetToastWacht(true)}
					onPointerLeave={() => zetToastWacht(false)}
					onFocus={(e) => {
						zetToastWacht(true)
						const van = e.relatedTarget as HTMLElement | null
						if (!focusErinRef.current && !e.currentTarget.contains(van))
							herkomstRef.current = van
						focusErinRef.current = true
						pilIdRef.current = getoond.id
					}}
					onBlur={(e) => {
						if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
						zetToastWacht(false)
						// de focus ging zelf ergens anders heen: niets terug te zetten
						if (e.relatedTarget) {
							focusErinRef.current = false
							herkomstRef.current = null
						}
					}}
				>
					<span className="heute-toast-tekst">{getoond.tekst}</span>
					{getoond.knop ? (
						<button
							type="button"
							className="heute-toast-knop"
							onClick={() => {
								getoond.knop?.doe()
								verbergToast(getoond.id)
							}}
						>
							{getoond.knop.label}
						</button>
					) : null}
				</div>
			) : null}
		</div>
	)
}
